import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import type { ClientListResponse } from '../models/client.model';
import type { ReminderListResponse, ReminderMutationResponse } from '../models/reminder.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly api = inject(ApiService);

  /**
   * List users/clients with optional search and pagination
   * @param params - Optional query parameters (search, page, limit, etc.)
   * @returns Observable of paginated client list
   */
  getUsers(params?: Record<string, string | number | boolean>): Observable<ClientListResponse> {
    return this.api.get<ClientListResponse>('admin/users', params);
  }

  /**
   * Get user by ID
   * @param id - User ID
   * @returns Observable of user
   */
  getUser(id: string): Observable<any> {
    return this.api.get<any>(`admin/users/${id}`);
  }
  /**
   * Get user accounts by user ID
   * @param id - User ID
   * @returns Observable of user accounts
   */
  getUserAccounts(id: string): Observable<any> {
    return this.api.get<any>('admin/user-accounts', { id });
  }

  /**
   * Get user account requests by user ID
   * @param id 
   * @returns 
   */
  getUserAccountRequests(id: string): Observable<any> {
    return this.api.get<any>(`admin/users/${id}/account-requests`);
  }

  /**
   * Add a new account for a user
   * @param userId - User ID
   * @param payload - Account details
   * @returns Observable of the response
   */
  addUserAccount(userId: string, payload: any): Observable<any> {
    return this.api.post<any>(`admin/users/${userId}/accounts`, payload);
  }

  /**
   * Get KYC Overview by user ID
   */
  getKycOverview(userId: string): Observable<any> {
    return this.api.get<any>(`admin/users/${userId}/kyc`);
  }

  /**
   * Get Requested KYC Documents by user ID
   */
  getRequestedKycDocuments(userId: string): Observable<any> {
    return this.api.get<any>(`admin/users/${userId}/kyc/requested-documents`);
  }

  /**
   * Get KYC document URL/details
   */
  getKycDocument(userId: string, docId: string): Observable<Blob> {
    // The ApiService currently only wraps standard JSON GET requests.
    // We inject HttpClient directly here just for this blob request.
    const url = `${this.api['baseUrl']}admin/users/${userId}/kyc-docs/${docId}`;
    return this.api['http'].get(url, { responseType: 'blob' });
  }

  /**
   * Review a specific KYC document
   */
  reviewKycDocument(userId: string, docId: string, payload: any): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/kyc/documents/${docId}`, payload);
  }

  /**
   * Review KYC Profile
   */
  reviewKycProfile(userId: string, payload: any): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/kyc/profile`, payload);
  }

  /**
   * Add KYC Document
   */
  addKycDocument(userId: string, formData: FormData): Observable<any> {
    return this.api.post<any>(`admin/users/${userId}/kyc/documents`, formData);
  }

  /**
   * Complete Profile Setup
   */
  completeSetup(userId: string): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/complete-setup`, {});
  }

  /**
   * Resubmit KYC Document
   * POST /api/v1/admin/users/:userId/kyc/documents/:imageId/resubmit
   */
  resubmitKycDocument(userId: string, imageId: string, formData: FormData): Observable<any> {
    return this.api.post<any>(`admin/users/${userId}/kyc/documents/${imageId}/resubmit`, formData);
  }

  /**
   * Request KYC Document
   * POST /api/v1/admin/users/:userId/kyc/request-document
   */
  requestKycDocument(userId: string, payload: { step_label: string, description: string }): Observable<any> {
    return this.api.post<any>(`admin/users/${userId}/kyc/request-document`, payload);
  }

  /**
   * Get user transactions by user ID
   * @param userId - User ID
   * @returns Observable of user transactions
   */
  getUserTransactions(userId: string): Observable<any> {
    return this.api.get<any>(`admin/users/${userId}/transactions`);
  }

  /**
   * Get user sessions by user ID
   * @param userId - User ID
   * @param params - Optional query parameters (page, limit, etc.)
   * @returns Observable of paginated user sessions
   */
  getUserSessions(userId: string, params?: Record<string, string | number | boolean>): Observable<any> {
    return this.api.get<any>(`admin/users/${userId}/sessions`, params);
  }

  /**
   * List Sessions
   * GET /sessions
   */
  listSessions(): Observable<any> {
    return this.api.get<any>(`admin/sessions`);
  }

  /**
   * Logout Others
   * POST /sessions/logout-others
   */
  logoutOthers(): Observable<any> {
    return this.api.post<any>(`admin/sessions/logout-others`, {});
  }

  /**
   * Delete Session
   * DELETE /sessions/:sessionId
   */
  deleteSession(sessionId: string): Observable<any> {
    return this.api.delete<any>(`admin/sessions/${sessionId}`);
  }

  /**
   * Change Password
   * POST /change-password
   */
  changePassword(data: any): Observable<any> {
    return this.api.post<any>(`admin/change-password`, data);
  }

  /**
   * Request Change Password OTP
   * POST /change-password/request-otp
   */
  requestChangePasswordOtp(payload: any): Observable<any> {
    return this.api.post<any>(`admin/change-password/request-otp`, payload);
  }

  /**
   * Confirm Change Password
   * POST /change-password/confirm
   */
  confirmChangePassword(payload: any): Observable<any> {
    return this.api.post<any>(`admin/change-password/confirm`, payload);
  }

  /**
   * Verify 2FA OTP
   * POST /admin/verify-2fa-otp
   */
  verify2Fa(data: { action: string, otp: string }): Observable<any> {
    return this.api.post<any>(`admin/verify-2fa-otp`, data);
  }

  /**
   * Update profile settings
   * PATCH /admin/profile-settings
   */
  updateProfileSettings(formData: FormData): Observable<any> {
    return this.api.patch<any>(`admin/profile-settings`, formData);
  }

  /**
   * Get profile settings
   * GET /admin/profile-settings
   */
  getProfileSettings(): Observable<any> {
    return this.api.get<any>(`admin/profile-settings`);
  }

  /**
   * Delete profile image
   * DELETE /admin/profile-image
   */
  deleteProfileImage(): Observable<any> {
    return this.api.delete<any>(`admin/profile-image`);
  }

  /**
   * Toggle Crypto Status
   * POST /admin/users/:userId/crypto-toggle
   */
  toggleCryptoStatus(userId: string, isCrypto: boolean): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/crypto-toggle`, { is_crypto: isCrypto });
  }

  /**
   * Freeze/Unfreeze User
   * PATCH /admin/users/freeze/:userId
   */
  freezeUser(userId: string, isFrozen: boolean): Observable<any> {
    return this.api.patch<any>(`admin/users/freeze/${userId}`, { is_frozen: String(isFrozen) });
  }

  /**
   * Get crypto account requests by user ID
   * @param id - User ID
   * @param params - Optional query parameters (page, limit, etc.)
   * @returns Observable of crypto account requests
   */
  getCryptoAccountRequests(id: string, params?: Record<string, string | number | boolean>): Observable<any> {
    return this.api.get<any>(`admin/users/${id}/crypto-account-requests`, params);
  }

  /**
   * Get user account balance by user ID
   * @param userId - User ID
   * @param params - Optional query parameters (page, limit, etc.)
   * @returns Observable of paginated account balances
   */
  getUserAccountBalance(userId: string, params?: Record<string, string | number | boolean>): Observable<any> {
    return this.api.get<any>(`admin/users/${userId}/account-balance`, params);
  }

  /**
   * Whitelist a crypto account request
   * @param id - Request ID
   * @returns Observable of the response
   */
  whitelistCryptoAccountRequest(id: string): Observable<any> {
    return this.api.patch<any>(`admin/crypto-account-requests/${id}/whitelist`, {});
  }

  /**
   * Update Sumsub Status (External KYC Provider)
   * PATCH /admin/users/:id/sumsub-status
   */
  updateSumsubStatus(userId: string, isRequired: boolean): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/sumsub-status`, { is_sumsub_required: isRequired });
  }

  /**
   * Submit Address
   * POST /admin/users/:userId/address
   */
  submitAddress(userId: string, payload: any): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/update-address`, payload);
  }

  /**
   * Get referral stats for insights
   * @param filter - Time filter (this_month, last_7_days, etc.)
   * @param country - Optional country filter
   * @returns Observable of referral distribution
   */
  getReferralStats(filter: string, country?: string): Observable<any> {
    const params: any = { filter };
    if (country) {
      params.country = country;
    }
    return this.api.get<any>('admin/users/referral-stats', params);
  }

  /**
   * Get card stats for insights
   * @param filter - Time filter (this_month, last_7_days, etc.)
   * @returns Observable of card distribution
   */
  getCardStats(filter: string): Observable<any> {
    return this.api.get<any>('admin/users/card-stats', { filter });
  }

  /**
   * Get profile update requests by user ID
   * @param id - User ID
   * @returns Observable of profile update requests
   */
  getProfileUpdateRequests(id: string): Observable<any> {
    return this.api.get<any>(`admin/users/${id}/profile-update-requests`);
  }

  /**
   * List admin reminders
   */
  getAdminReminders(userId: string, params?: Record<string, string | number | boolean>): Observable<ReminderListResponse> {
    return this.api.get<ReminderListResponse>(`admin/users/${userId}/reminders`, params);
  }

  /**
   * Create an admin reminder
   */
  createAdminReminder(userId: string, payload: { reminder_date: string; title: string; description: string }): Observable<ReminderMutationResponse> {
    return this.api.post<ReminderMutationResponse>(`admin/users/${userId}/reminders`, payload);
  }

  /**
   * Update an admin reminder
   */
  updateAdminReminder(userId: string, reminderId: string, payload: { reminder_date: string; title: string; description: string }): Observable<ReminderMutationResponse> {
    return this.api.patch<ReminderMutationResponse>(`admin/users/${userId}/reminders/${reminderId}`, payload);
  }

  /**
   * Delete an admin reminder
   */
  deleteAdminReminder(userId: string, reminderId: string): Observable<ReminderMutationResponse> {
    return this.api.delete<ReminderMutationResponse>(`admin/users/${userId}/reminders/${reminderId}`);
  }

  /**
   * Review a profile update request
   * @param userId - User ID
   * @param payload - Action payload (approve/reject) and request type
   * @returns Observable of the response
   */
  reviewProfileUpdateRequest(userId: string, payload: { action: string, type: string }): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/profile-requests/update`, payload);
  }

  /**
   * Update user account details
   * @param userId - User ID
   * @param accountId - Account ID
   * @param payload - Updated account details
   * @returns Observable of the response
   */
  updateUserAccount(userId: string, accountId: string, payload: any): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/accounts/${accountId}`, payload);
  }

  /**
   * Import Sumsub Applicant
   * POST /admin/users/:userId/kyc/import-sumsub-applicant/:applicantId
   * @param userId - User ID
   * @param applicantId - Sumsub Applicant ID
   * @param confirmUpdate - Flag to confirm the update (defaults to false for preview)
   */
  importSumsubApplicant(userId: string, applicantId: string, confirmUpdate: boolean = false): Observable<any> {
    return this.api.post<any>(`admin/users/${userId}/kyc/import-sumsub-applicant/${applicantId}`, {
      confirm_update: confirmUpdate
    });
  }

  /**
   * Update Sumsub Active State (KYC Presence)
   * PATCH admin/users/:id/kyc/presence
   */
  updateKycPresence(userId: string, status: 'activated' | 'deactivated'): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/kyc/presence`, { status });
  }

  /**
   * Add Crypto Account (Wallet Address)
   * POST admin/users/:userId/add-crypto
   */
  addCryptoAccount(userId: string, payload: any): Observable<any> {
    return this.api.post<any>(`admin/users/${userId}/crypto-account-requests`, payload);
  }

  /**
   * Update User Risk Profile
   * PATCH admin/users/:userId/risk-profile
   */
  updateRiskLevel(userId: string, riskLevel: string): Observable<any> {
    return this.api.patch<any>(`admin/users/${userId}/risk-profile`, { risk_level: riskLevel });
  }

  /**
   * Block/Unblock User
   * PATCH admin/users/block/:userId
   */
  blockUser(userId: string, isBlocked: boolean): Observable<any> {
    return this.api.patch<any>(`admin/users/block/${userId}`, { is_blocked: isBlocked });
  }

  /**
   * Mark an admin reminder as completed
   */
  completeAdminReminder(userId: string, reminderId: string): Observable<ReminderMutationResponse> {
    return this.api.patch<ReminderMutationResponse>(`admin/users/${userId}/reminders/${reminderId}/complete`, {});
  }

  /**
   * Upload expiry requested document
   * POST /admin/users/kyc/:id/expiry-requested-docs/:documentId
   */
  uploadExpiryRequestedDoc(userId: string, documentId: string, formData: FormData): Observable<any> {
    return this.api.post<any>(`admin/users/kyc/${userId}/expiry-requested-docs/${documentId}`, formData);
  }

  /**
   * Get user activity logs
   * @param params - Optional query parameters (customerId, fromDate, toDate, etc.)
   * @returns Observable of user activity logs
   */
  getUserActivityLogs(params?: Record<string, string | number | boolean>): Observable<any> {
    return this.api.get<any>('admin/user-activity-logs', params);
  }

  /**
   * Get user activity by ID
   * @param id - The user's internal ID
   */
  getUserActivityById(id: string): Observable<any> {
    return this.api.get<any>(`admin/user-activity/${id}`);
  }
}
